import { z } from "zod";
export const MailboxFolderSchema = z.enum([
  "INBOX",
  "OUTBOX",
  "SENT",
  "DRAFTS",
  "ARCHIVE",
  "TRASH",
  "SPAM",
  "STARRED",
  "ALL",
]);
export const MailboxDirectionSchema = z.enum(["INBOUND", "OUTBOUND", "ALL"]);
export const MailboxStatusSchema = z.enum([
  "RECEIVED",
  "PROCESSING",
  "PROCESSED",
  "FAILED",
  "REJECTED",
  "DRAFT",
  "QUEUED",
  "SENDING",
  "SENT",
  "DELIVERED",
  "BOUNCED",
  "DELIVERY_UNKNOWN",
]);
const id = z.string().min(1).max(256),
  nullableText = z.string().nullish(),
  count = z.number().int().nonnegative();
export const MailboxReadQuerySchema = z
  .object({
    folder: MailboxFolderSchema.default("ALL"),
    direction: MailboxDirectionSchema.default("ALL"),
    status: MailboxStatusSchema.optional(),
    isRead: z.boolean().optional(),
    isStarred: z.boolean().optional(),
    search: z.string().trim().max(200).optional(),
    partnerId: id.optional(),
    documentId: id.optional(),
    limit: z.number().int().min(1).max(100).default(50),
    offset: z.number().int().min(0).max(100000).default(0),
    sortBy: z.enum(["date", "subject", "sender"]).default("date"),
    sortOrder: z.enum(["asc", "desc"]).default("desc"),
  })
  .strict();
export const MailboxStatsQuerySchema = MailboxReadQuerySchema.pick({
  direction: true,
  search: true,
});
export const MailboxAttachmentReadSchema = z.object({
  id: id.nullish(),
  filename: z.string(),
  contentType: nullableText,
  size: z.number().nonnegative().nullish(),
  r2Key: nullableText,
  contentHash: nullableText,
  documentId: id.nullish(),
});
export const MailboxMessageSchema = z.object({
  id,
  tenantId: id,
  direction: z.enum(["INBOUND", "OUTBOUND"]),
  fromEmail: z.string(),
  fromName: nullableText,
  toEmail: z.string(),
  toName: nullableText,
  subject: z.string(),
  snippet: nullableText,
  attachments: z.array(MailboxAttachmentReadSchema),
  status: MailboxStatusSchema,
  isRead: z.boolean(),
  isStarred: z.boolean(),
  isArchived: z.boolean(),
  folder: z.enum([
    "INBOX",
    "OUTBOX",
    "SENT",
    "DRAFTS",
    "ARCHIVE",
    "TRASH",
    "SPAM",
  ]),
  documentId: id.nullish(),
  workflowTraceId: id.nullish(),
  partnerId: id.nullish(),
  provider: nullableText,
  selectedTransport: nullableText,
  selectedProviderId: nullableText,
  attemptCount: count,
  lastErrorCode: nullableText,
  nextAttemptAt: nullableText,
  sentAt: nullableText,
  deliveredAt: nullableText,
  bouncedAt: nullableText,
  bounceReason: nullableText,
  errorMessage: nullableText,
  receivedAt: nullableText,
  rawArchiveEntryId: nullableText,
  deliveryArchiveEntryId: nullableText,
  archiveEntryIds: z.array(z.string()),
  complianceArchived: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  date: nullableText,
});
export const MailboxDetailSchema = MailboxMessageSchema.extend({
  bodyHtml: nullableText,
  bodyText: nullableText,
  ccEmails: z.array(z.string()),
  bccEmails: z.array(z.string()),
  providerMessageId: nullableText,
  localCaptureAvailable: z.boolean().optional(),
  rawEmailR2Key: nullableText,
  relatedDocument: z
    .object({
      id,
      documentNumber: nullableText,
      type: z.string(),
      status: z.string(),
      totalGrossAmount: z.number().nullish(),
      currency: nullableText,
      issueDate: nullableText,
    })
    .nullable(),
  relatedWorkflow: z
    .object({
      id,
      workflowId: z.string(),
      status: z.string(),
      startTime: z.union([z.string(), z.number()]).nullish(),
      endTime: z.union([z.string(), z.number()]).nullish(),
      currentNodeId: nullableText,
    })
    .nullable(),
});
export const MailboxPageSchema = z.object({
  data: z.array(MailboxMessageSchema).max(100),
  total: count,
  pagination: z.object({
    limit: z.number().int().min(1).max(100),
    offset: count,
  }),
});
export const MailboxDetailResponseSchema = z.object({
  data: MailboxDetailSchema,
});
export const MailboxReadStatsSchema = z.object({
  inbox: z.object({ total: count, unread: count }),
  sent: z.object({
    total: count,
    delivered: count,
    failed: count,
    deliveryUnknown: count,
    pending: count,
  }),
  outbox: count,
  drafts: count,
  archive: count,
  trash: count,
  spam: count,
  starred: count,
  processing: count,
  withAttachments: count,
  linkedToDocuments: count,
  complianceArchived: count,
});
export const MailboxStatsResponseSchema = z.object({
  data: MailboxReadStatsSchema,
});
export const MailboxBulkCommandSchema = z
  .object({
    emailIds: z.array(z.string().uuid()).min(1).max(100),
    action: z.enum([
      "MARK_READ",
      "MARK_UNREAD",
      "STAR",
      "UNSTAR",
      "ARCHIVE",
      "UNARCHIVE",
      "TRASH",
      "RESTORE",
      "DELETE_PERMANENT",
      "MARK_SPAM",
      "MARK_NOT_SPAM",
    ]),
  })
  .strict();
export const MailboxPatchCommandSchema = z
  .object({
    isRead: z.boolean().optional(),
    isStarred: z.boolean().optional(),
    isArchived: z.boolean().optional(),
    folder: z
      .enum(["INBOX", "SENT", "DRAFTS", "ARCHIVE", "TRASH", "SPAM"])
      .optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, "No updates provided");
export const MailboxBulkResultSchema = z.object({
  success: z.literal(true),
  affectedIds: z.array(id),
  action: MailboxBulkCommandSchema.shape.action,
});
export const MailboxPatchResultSchema = z.object({ success: z.literal(true) });
export type MailboxReadQuery = z.infer<typeof MailboxReadQuerySchema>;
export type MailboxMessage = z.infer<typeof MailboxMessageSchema>;
export const MailboxRetryCommandSchema = z
  .object({ acknowledgeDuplicateRisk: z.boolean().default(false) })
  .strict();
export const MailboxRetryResultSchema = z.object({
  data: z.object({ id, status: MailboxStatusSchema }),
});

export const MailboxComposeCommandSchema = z
  .object({
    to: z.array(z.string().email()).min(1).max(10),
    cc: z.array(z.string().email()).max(10).default([]),
    bcc: z.array(z.string().email()).max(10).default([]),
    subject: z.string().min(1).max(200),
    bodyHtml: z.string().max(1024 * 1024),
    bodyText: z
      .string()
      .max(1024 * 1024)
      .optional(),
    attachments: z
      .array(
        z.object({
          filename: z.string().min(1).max(255),
          contentType: z.string().min(1).max(128),
          r2Key: z.string().min(1).max(1024),
          size: z
            .number()
            .int()
            .nonnegative()
            .max(10 * 1024 * 1024),
        }),
      )
      .max(10)
      .default([]),
    documentId: z.string().uuid().optional(),
    isDraft: z.boolean().default(false),
    inReplyTo: z.string().uuid().optional(),
    scheduledAt: z.string().datetime().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.to.length + value.cc.length + value.bcc.length > 20) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_big,
        type: "array",
        maximum: 20,
        inclusive: true,
        path: ["to"],
        message: "Too many recipients",
      });
    }
  });

export const MailboxComposeResultSchema = z.object({
  success: z.literal(true),
  data: z.object({
    id,
    status: MailboxStatusSchema,
    transport: z.string().optional(),
    provider: z.string().optional(),
    message: z.string(),
  }),
});
